import type { StandingsTable } from '@/core/engine/contracts';
import type { Team, TeamId } from '@/core/schemas';
import { cn } from '@/shared/utils/cn';

interface StandingsTableViewProps {
  table: StandingsTable;
  lookup: (teamId: TeamId) => Team | undefined;
  qualifyingRanks?: number;
  highlightTeamId?: TeamId | undefined;
}

export function StandingsTableView({
  table,
  lookup,
  qualifyingRanks = 0,
  highlightTeamId,
}: StandingsTableViewProps) {
  return (
    <section className="border-line bg-surface overflow-hidden rounded-xl border">
      <h3 className="border-line border-b px-4 py-3 text-sm font-semibold">{table.label}</h3>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] text-sm">
          <thead>
            <tr className="text-fg-subtle text-xs tracking-wide uppercase">
              <th scope="col" className="w-8 px-3 py-2 text-left font-medium">
                #
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Team
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                Sp
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                S
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                U
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                N
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                Legs
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium">
                Diff
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Pkt
              </th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => {
              const qualifies = qualifyingRanks > 0 && row.rank <= qualifyingRanks;
              const isOwn = row.teamId === highlightTeamId;

              return (
                <tr
                  key={row.teamId}
                  className={cn(
                    'border-line border-t',
                    qualifies && 'bg-success-subtle/40',
                    isOwn && 'bg-brand-subtle',
                  )}
                >
                  <td className="text-fg-muted px-3 py-2">{row.rank}</td>
                  <td className={cn('truncate px-3 py-2', isOwn && 'text-brand font-semibold')}>
                    {lookup(row.teamId)?.name ?? row.teamId}
                  </td>
                  <td className="px-2 py-2 text-right">{row.played}</td>
                  <td className="px-2 py-2 text-right">{row.won}</td>
                  <td className="px-2 py-2 text-right">{row.drawn}</td>
                  <td className="px-2 py-2 text-right">{row.lost}</td>
                  <td className="text-fg-muted px-2 py-2 text-right">
                    {row.legsFor}:{row.legsAgainst}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {row.legDifference > 0 ? `+${row.legDifference}` : row.legDifference}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{row.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
